import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, TextField, Button, Grid } from "@mui/material";
import {
	ShareHoldingApi,
	getShareHoldingApi,
	editShareHoldingApi,
	editDirectorsApi,
} from "../../apiServices/services/OfficeServices";
import { updateFormStatus } from "../../apiServices/services/ProfileServices";
import { getDirectorsApi } from "../../apiServices/services/OfficeServices";

const ShareHolding = ({ onContinue, onBack }) => {
	const [formData, setFormData] = useState({
		initialPaidCapital: 0,
		shareValue: "",
		totalShares: 0,
		totalAmountUnpaid: 0,
		totalNominalValue: 0,
		currency: "GBP",
		shareClass: "Ordinary",
		shareHolders: [],
		isFormCompleted: false,
		userId: localStorage.getItem("userId"),
	});

	const shareHoldingRef = useRef(null);
	const [userId, setUserId] = useState(0);
	const [directorCollectionId, setDirectorCollectionId] = useState(0);
	const [directorsArray, setDirectors] = useState([]);

	useEffect(() => {
		getDirectorsApi(formData.userId).then((response) => {
			if (response?.attributes?.results[0]) {
				setDirectorCollectionId(response?.attributes?.results[0]?.id);
				const shareholdersArray =
					response.attributes?.results[0]?.shareHolders.filter(
						(d) =>
							d.position === "Shareholder Only" ||
							d.position === "Director + Shareholder"
					);
				const directorArr =
					response.attributes?.results[0]?.shareHolders.filter(
						(d) => d.position === "Director Only"
					);
				setDirectors(directorArr);
				setFormData((prevState) => ({
					...prevState,
					shareHolders: shareholdersArray,
				}));
			}
		});

		getShareHoldingApi(formData.userId).then((response) => {
			const result = response?.attributes?.results?.[0];
			if (result) {
				// Populate formData if shareholding data exists
				setUserId(result.id);
				setFormData((prevState) => ({
					...prevState,
					initialPaidCapital: result.initialPaidCapital,
					isFormCompleted: result.isFormCompleted,
					totalShares: result.totalShares,
					shareValue: result.shareValue,
					totalAmountUnpaid: result.totalAmountUnpaid,
					totalNominalValue: result.totalNominalValue,
					currency: result.currency,
					shareClass: result.shareClass,
				}));
			}
		});
	}, []);

	useEffect(() => {
		setFormData((prevFormData) => {
			const totalShares = prevFormData.totalShares;
			const shareholders = prevFormData.shareHolders;

			const totalAmountUnpaid = totalShares * formData.shareValue;

			const totalNominalValue = shareholders.reduce(
				(sum, s) => sum + s.noOfShare * formData.shareValue,
				0
			);

			const initialPaidCapital = totalShares * (formData.shareValue ?? 0);

			return {
				...prevFormData,
				initialPaidCapital,
				totalShares,
				totalAmountUnpaid,
				totalNominalValue,
				currency: "GBP",
				shareClass: "Ordinary",
			};
		});
	}, [formData.shareValue]);

	const handleClassChange = (index, key, value) => {
		setFormData((prevFormData) => {
			const updatedShareClasses = prevFormData.shareHolders.map(
				(shareHolders, i) =>
					i === index
						? { ...shareHolders, [key]: value }
						: shareHolders
			);
			const totalShares = updatedShareClasses.reduce((acc, curr) => {
				return acc + Number(curr.noOfShare);
			}, 0);

			const totalAmountUnpaid = totalShares * formData.shareValue;

			const totalNominalValue = updatedShareClasses.reduce(
				(sum, s) => sum + s.noOfShare * formData.shareValue,
				0
			);

			const initialPaidCapital = totalShares * (formData.shareValue ?? 0);

			return {
				...prevFormData,
				initialPaidCapital,
				shareHolders: updatedShareClasses,
				totalShares,
				totalAmountUnpaid,
				totalNominalValue,
				currency: "GBP",
				shareClass: "Ordinary",
			};
		});
	};

	function determinePscStatus(noOfShare) {
		const percentage = (noOfShare / formData.totalShares) * 100;

		if (percentage > 25 && percentage <= 50) {
			return {
				isPsc: true,
				natureOfControl: "OWNERSHIPOFSHARES_25TO50PERCENT",
			};
		} else if (percentage > 50 && percentage <= 75) {
			return {
				isPsc: true,
				natureOfControl: "OWNERSHIPOFSHARES_50TO75PERCENT",
			};
		} else if (percentage > 75) {
			return {
				isPsc: true,
				natureOfControl: "OWNERSHIPOFSHARES_75TO100PERCENT",
			};
		} else {
			return {
				isPsc: false,
				natureOfControl: "",
			};
		}
	}

	const handleSubmit = () => {
		if (!formData.shareValue) {
			alert(`Please fill Share Value for Shareholding`);
			return;
		}

		for (let i = 0; i < formData.shareHolders.length; i++) {
			const { noOfShare } = formData.shareHolders[i];
			if (!noOfShare) {
				alert(`Please fill all the details for Shareholder ${i + 1}`);
				return;
			}
		}

		formData.shareHolders = formData.shareHolders.map((shareholder) => {
			const pscStatus = determinePscStatus(shareholder.noOfShare);
			return {
				...shareholder,
				isPsc: pscStatus.isPsc,
				natureOfControl: pscStatus.natureOfControl,
			};
		});

		const directorData = {
			shareHolders: [...formData.shareHolders, ...directorsArray],
			isFormCompleted: formData.isFormCompleted,
			userId: formData.userId,
		};

		if (!formData.isFormCompleted) {
			ShareHoldingApi({ ...formData, isFormCompleted: true }).then(
				(res) => {
					if (res.status === 200) {
						updateFormStatus(formData.userId, {
							isShareholdingDetailsCompleted: true,
						}).then((res) => {
							if (res.status === 200) {
								onContinue();
								localStorage.setItem(
									"activeForm",
									"isShareholdingDetailsCompleted"
								);
							}
						});
					}
				}
			);

			editDirectorsApi(directorCollectionId, directorData).then((res) => {
				if (res.status === 200) {
					console.log("Updated", res);
				}
			});
		} else {
			editShareHoldingApi(userId, { ...formData }).then((res) => {
				if (res.status === 200) {
					onContinue();
					localStorage.setItem(
						"activeForm",
						"isShareholdingDetailsCompleted"
					);
				}
			});
			editDirectorsApi(directorCollectionId, directorData).then((res) => {
				if (res.status === 200) {
					console.log("Updated", res);
				}
			});
		}
	};

	return (
		<Box
			sx={{
				padding: 4,
				backgroundColor: "#fff",
				borderRadius: 2,
				margin: "auto",
			}}>
			<Typography
				variant='h6'
				sx={{
					color: "1C2434",
					fontWeight: "bold",
					fontSize: "1.25rem",
				}}>
				Shareholding
			</Typography>
			<Typography
				variant='body1'
				sx={{
					fontSize: "1rem",
					color: "2F2D3B",
					mb: 3,
					mt: 2,
					maxWidth: "555px",
					width: "100%",
				}}>
				Below we have provided the number of shareholders outlined in
				the previous Directors section.
			</Typography>
			<Typography
				variant='body1'
				sx={{
					fontSize: "1rem",
					color: "2F2D3B",
					mb: 1,
					mt: 2,
					fontWeight: "bold",
				}}>
				Initial Paid-up capital
			</Typography>
			<Typography
				variant='body1'
				sx={{
					fontSize: "1rem",
					color: "2F2D3B",
					mb: 3,
					mt: 1,
					maxWidth: "555px",
					width: "100%",
				}}>
				After incorporation, you must deposit at least £1 of paid-up
				capital into your company bank account. We recommend this
				remains at £1 for incorporation purposes. ​
			</Typography>

			<Typography
				variant='body1'
				sx={{
					fontSize: "1rem",
					fontWeight: "medium",
					color: "2F2D3B",
					marginTop: "1.2rem",
					mb: 1,
				}}>
				Share Value
			</Typography>
			<TextField
				placeholder='Enter Share Value'
				name='shareValue'
				value={formData?.shareValue}
				onChange={(e) =>
					setFormData((prevData) => ({
						...prevData,
						shareValue: e.target.value,
					}))
				}
				sx={{
					maxWidth: "555px",
					width: "100%",
					mb: "1rem",
				}}
			/>

			{formData.shareHolders.map((cls, index) => (
				<Box key={index} ref={shareHoldingRef}>
					<Typography
						variant='body1'
						sx={{
							fontSize: "1rem",
							color: "2F2D3B",
							mb: 1.5,
							mt: 0.5,
							fontWeight: "bold",
						}}>
						Shareholder {index + 1}
					</Typography>
					<TextField
						name='name'
						value={`${formData?.shareHolders[index].firstName} ${formData?.shareHolders[index].surName}`}
						disabled
						sx={{
							maxWidth: "260px",
							width: "100%",
							mb: "1rem",
							mr: 4,
						}}
					/>

					<TextField
						placeholder='Enter No. of Shares'
						name='noOfShare'
						value={formData?.shareHolders[index]?.noOfShare}
						onChange={(e) =>
							handleClassChange(
								index,
								"noOfShare",
								e.target.value
							)
						}
						sx={{
							maxWidth: "260px",
							width: "100%",
							mb: "1rem",
							mr: 5,
						}}
					/>
				</Box>
			))}
			<Typography
				variant='body1'
				sx={{
					fontSize: "1rem",
					color: "2F2D3B",
					mb: 1,
					mt: 2,
					fontWeight: "bold",
				}}>
				Initial Paid-up capital
			</Typography>
			<TextField
				fullWidth
				placeholder='£0'
				name='initialPaidCapital'
				value={formData?.initialPaidCapital}
				onChange={(e) =>
					setFormData((prevData) => ({
						...prevData,
						initialPaidCapital: e.target.value,
					}))
				}
				disabled
				sx={{ maxWidth: "555px", width: "100%", mb: "1rem", mr: 4 }}
			/>
			{/* TODO: PSC */}
			{/* {formData.shareHolders[0].noOfShares == "100" && (
				<Box sx={{ display: "flex", mt: 1 }}>
					{" "}
					<img src={TickMark} />
					<Typography
						sx={{ color: "#2069F8", fontWeight: "bold", ml: 1 }}>
						This person will be deemed a Person of Significant
						Control on Companies House​
					</Typography>
				</Box>
			)} */}

			<Typography
				variant='body1'
				sx={{
					fontSize: "1.1rem",
					fontWeight: "bold",
					color: "#2F2D3B",
					maxWidth: "745px",
					width: "100%",
					mt: 4,
					mb: 1,
				}}>
				Statement Of Capital
			</Typography>

			<Box
				sx={{
					display: "flex",
					width: "100%",
				}}>
				<Box mr={5}>
					<Typography
						variant='body1'
						sx={{
							fontSize: "1rem",
							fontWeight: "medium",
							color: "2F2D3B",
							marginTop: "1.2rem",
							mb: 1,
						}}>
						Total Shares
					</Typography>
					<TextField
						name='totalShares'
						fullWidth
						variant='outlined'
						value={formData.totalShares}
						disabled
						sx={{
							width: "260px",
						}}
					/>
				</Box>
				<Box>
					<Typography
						variant='body1'
						sx={{
							fontSize: "1rem",
							fontWeight: "medium",
							color: "2F2D3B",
							marginTop: "1.2rem",
							mb: 1,
						}}>
						Total Amount Unpaid
					</Typography>
					<TextField
						name='totalAmountUnpaid'
						fullWidth
						variant='outlined'
						value={formData.totalAmountUnpaid}
						disabled
						sx={{
							width: "260px",
						}}
					/>
				</Box>
			</Box>

			<Box
				sx={{
					display: "flex",
					width: "550px",
					gap: "40px",
				}}>
				<Box>
					<Typography
						variant='body1'
						sx={{
							fontSize: "1rem",
							fontWeight: "medium",
							color: "2F2D3B",
							marginTop: "1.2rem",
							mb: 1,
						}}>
						Currency
					</Typography>
					<TextField
						name='currency'
						fullWidth
						variant='outlined'
						value={formData.currency}
						disabled
						sx={{
							width: "260px",
						}}
					/>
				</Box>
				<Box>
					<Typography
						variant='body1'
						sx={{
							fontSize: "1rem",
							fontWeight: "medium",
							color: "2F2D3B",
							marginTop: "1.2rem",
							mb: 1,
						}}>
						Share Class
					</Typography>
					<TextField
						name='shareClass'
						fullWidth
						variant='outlined'
						value={formData.shareClass}
						disabled
						sx={{
							width: "260px",
						}}
					/>
				</Box>
			</Box>

			<Box>
				<Typography
					variant='body1'
					sx={{
						fontSize: "1rem",
						fontWeight: "medium",
						color: "2F2D3B",
						marginTop: "1.2rem",
						mb: 1,
					}}>
					Total Share Value
				</Typography>
				<TextField
					name='totalNominalValue'
					fullWidth
					variant='outlined'
					value={formData.totalNominalValue}
					disabled
					sx={{
						width: "560px",
					}}
				/>
			</Box>

			<Grid item xs={12}>
				<Box mt={3} display='flex'>
					<Button
						sx={{
							marginTop: "1rem",
							textTransform: "none",
							backgroundColor: "#ffffff",
							color: "#cfcfcf",
							border: "1px solid #cfcfcf",
							borderRadius: "1rem",
							padding: "0.4rem 1.5rem",
							fontWeight: 600,
							"&:hover": {
								color: "#2069F8",
								border: "1px solid #2069F8",
							},
						}}
						onClick={onBack}>
						Previous
					</Button>
					<Button
						sx={{
							marginTop: "1rem",
							marginLeft: "0.5rem",
							borderRadius: "1rem",
							backgroundColor: "#2069F8",
							color: "#ffffff",
							fontWeight: 700,
							padding: "0.5rem 1.5rem",
							textTransform: "none",
							width: "max-content",
							"&:hover": {
								backgroundColor: "#2069F8",
							},
						}}
						onClick={handleSubmit}>
						Continue
					</Button>
				</Box>
			</Grid>
		</Box>
	);
};

export default ShareHolding;
